import React, { useState, useMemo, useEffect } from 'react';
import { FileSpreadsheet, Mail, Send, Target, Database, TrendingUp, ShoppingCart, CheckCircle } from 'lucide-react';
import { useAuthenticator } from '@aws-amplify/ui-react';
import { fetchAuthSession } from 'aws-amplify/auth';
import { StripeCheckoutButton } from '../StripeCheckoutButton';
import { MyOrders } from '../MyOrders';
import fipsData from '../../data/fips.json';

// Type for FIPS data structure
type FipsRecord = {
  COUNTY: string;
  FIPS: string;
  STATE: string;
};

type FipsData = {
  [key: string]: FipsRecord;
};

interface TaxDelinquentListsViewProps {
  initialParams?: {
    orderId?: string;
  };
}

export function TaxDelinquentListsView({ initialParams }: TaxDelinquentListsViewProps = {}) {
  const { user } = useAuthenticator();

  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    state: '',
    county: '',
    message: ''
  });

  const [purchaseData, setPurchaseData] = useState({
    state: '',
    county: ''
  });

  const [inquiryStatus, setInquiryStatus] = useState<{
    type: 'success' | 'error' | null;
    message: string;
  }>({ type: null, message: '' });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showThankYouModal, setShowThankYouModal] = useState(false);

  // Auto-fill user info from Cognito
  useEffect(() => {
    if (user?.signInDetails?.loginId) {
      const userName = user.username || '';
      const userEmail = user.signInDetails.loginId;
      const userPhone = (user as any)?.attributes?.phone_number || '';

      setFormData(prev => ({
        ...prev,
        email: userEmail,
        name: userName,
        phone: userPhone
      }));
    }
  }, [user]);

  // Parse fips data
  const typedFipsData = fipsData as FipsData;

  // Get unique states sorted alphabetically
  const states = useMemo(() => {
    const stateSet = new Set<string>();
    Object.values(typedFipsData).forEach((record) => {
      stateSet.add(record.STATE);
    });
    return Array.from(stateSet).sort();
  }, []);

  // Get counties for selected state (for purchase dropdown)
  const purchaseCounties = useMemo(() => {
    if (!purchaseData.state) return [];
    const counties = Object.values(typedFipsData)
      .filter((record) => record.STATE === purchaseData.state)
      .map((record) => record.COUNTY)
      .sort();
    return counties;
  }, [purchaseData.state]);

  // Get counties for selected state (for contact form dropdown)
  const formCounties = useMemo(() => {
    if (!formData.state) return [];
    const counties = Object.values(typedFipsData)
      .filter((record) => record.STATE === formData.state)
      .map((record) => record.COUNTY)
      .sort();
    return counties;
  }, [formData.state]);

  // Check for payment success in URL
  const urlParams = new URLSearchParams(window.location.search);
  const paymentSuccess = urlParams.get('payment') === 'success';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setInquiryStatus({ type: null, message: '' });
    setIsSubmitting(true);

    console.log('Contact form submitted', { formData });

    try {
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken?.toString();

      console.log('Got auth token:', !!idToken);

      if (!idToken) {
        setInquiryStatus({
          type: 'error',
          message: 'Please log in to submit an inquiry'
        });
        setIsSubmitting(false);
        return;
      }

      const apiUrl = `${import.meta.env.VITE_API_URL}/contact/inquiry`;
      console.log('Calling API:', apiUrl);

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          state: formData.state,
          county: formData.county,
          message: formData.message
        })
      });

      console.log('Response status:', response.status);

      if (response.ok) {
        const data = await response.json();
        console.log('Success response:', data);

        // Show thank you modal
        setShowThankYouModal(true);

        setInquiryStatus({
          type: 'success',
          message: 'Request submitted successfully! We will respond within 4 hours.'
        });

        // Reset form (keep name/email/phone from user, clear selections and message)
        const userName = user?.username || '';
        const userEmail = user?.signInDetails?.loginId || '';
        const userPhone = (user as any)?.attributes?.phone_number || '';

        setFormData({
          name: userName,
          phone: userPhone,
          email: userEmail,
          state: '',
          county: '',
          message: ''
        });
      } else {
        const errorData = await response.json();
        console.error('Error response:', response.status, errorData);
        setInquiryStatus({
          type: 'error',
          message: errorData.error || 'Failed to submit inquiry. Please try again.'
        });
      }
    } catch (err) {
      console.error('Error submitting inquiry:', err);
      setInquiryStatus({
        type: 'error',
        message: 'Failed to submit inquiry. Please try again.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;

    // If state changes, reset county
    if (name === 'state') {
      setFormData({
        ...formData,
        state: value,
        county: ''
      });
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }
  };

  const handlePurchaseChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = e.target;

    // If state changes, reset county
    if (name === 'state') {
      setPurchaseData({
        state: value,
        county: ''
      });
    } else {
      setPurchaseData({
        ...purchaseData,
        [name]: value
      });
    }
  };

  // TaxLates brand colors
  const taxlatesOrange = '#f26638';
  const taxlatesBlue = '#3a7ed2';
  const taxlatesNavy = '#191d43';
  const taxlatesTeal = '#4a899a';

  // Check if purchase form is complete
  const isPurchaseFormComplete = purchaseData.state && purchaseData.county;

  return (
    <div className="space-y-6">
      {/* Payment Success Banner */}
      {paymentSuccess && (
        <div className="bg-green-100 border-l-4 border-green-500 p-4">
          <div className="flex items-center">
            <CheckCircle className="w-6 h-6 text-green-600 mr-3" />
            <div>
              <h3 className="text-lg font-bold text-green-800">Payment Successful!</h3>
              <p className="text-sm text-green-700">Your order has been received. Check "My Orders" below for delivery status (7-10 days).</p>
            </div>
          </div>
        </div>
      )}

      {/* Page Header with TaxLatesData Logo */}
      <div className="bg-white rounded-2xl shadow-lg p-6">
        <div className="flex items-center space-x-4 mb-4">
          <img
            src="https://taxlatesdata.com/images/taxlates-logo-00.png"
            alt="TaxLatesData"
            className="h-12 w-auto"
          />
        </div>
        <div>
          <h1 className="text-3xl font-bold" style={{ color: taxlatesNavy }}>Tax Delinquent Lists</h1>
          <p className="text-gray-600 mt-1">Request fresh tax delinquent data for your target markets</p>
        </div>
      </div>


      {/* Purchase All Records Section with Stripe */}
      <div className="bg-gradient-to-br from-green-50 to-blue-50 rounded-2xl shadow-lg p-8 border-2" style={{ borderColor: taxlatesOrange }}>
        <div className="flex items-center space-x-3 mb-6">
          <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: taxlatesOrange }}>
            <ShoppingCart className="w-6 h-6 text-white" />
          </div>
          <div>
            <h2 className="text-2xl font-bold" style={{ color: taxlatesNavy }}>Purchase Tax Delinquent Records</h2>
            <p className="text-sm text-gray-600">Get high-quality tax delinquent data for your county - Only $667</p>
          </div>
        </div>

        <div className="bg-white rounded-xl p-6">

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div>
              <label className="text-sm text-gray-700 font-semibold block mb-2">State *</label>
              <select
                name="state"
                value={purchaseData.state}
                onChange={handlePurchaseChange}
                required
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl text-sm"
                style={{ outline: 'none' }}
                onFocus={(e) => {
                  e.target.style.borderColor = taxlatesOrange;
                  e.target.style.boxShadow = `0 0 0 3px ${taxlatesOrange}20`;
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#d1d5db';
                  e.target.style.boxShadow = 'none';
                }}
              >
                <option value="">Select State</option>
                {states.map((state) => (
                  <option key={state} value={state}>{state}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="text-sm text-gray-700 font-semibold block mb-2">County *</label>
              <select
                name="county"
                value={purchaseData.county}
                onChange={handlePurchaseChange}
                required
                disabled={!purchaseData.state}
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
                style={{ outline: 'none' }}
                onFocus={(e) => {
                  e.target.style.borderColor = taxlatesOrange;
                  e.target.style.boxShadow = `0 0 0 3px ${taxlatesOrange}20`;
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#d1d5db';
                  e.target.style.boxShadow = 'none';
                }}
              >
                <option value="">
                  {purchaseData.state ? 'Select County' : 'Select a state first'}
                </option>
                {purchaseCounties.map((county) => (
                  <option key={county} value={county}>{county}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Always show Stripe checkout button (grayed out until selection) */}
          <StripeCheckoutButton
            state={purchaseData.state}
            county={purchaseData.county}
          />
        </div>
      </div>

      {/* Not Ready to Purchase - Contact Form */}
      <div className="bg-gradient-to-br from-orange-50 to-blue-50 rounded-2xl shadow-lg p-8 border-2" style={{ borderColor: taxlatesOrange }}>
        <div className="text-center mb-6">
          <h2 className="text-2xl font-bold mb-2" style={{ color: taxlatesNavy }}>Not Ready to Purchase Yet?</h2>
          <p className="text-gray-600">Please reach out if you have any questions</p>
        </div>

        <div className="bg-white rounded-xl p-6">
          {/* Status Message */}
          {inquiryStatus.type && (
            <div
              data-inquiry-status
              className={`mb-6 p-4 rounded-xl border-2 ${
                inquiryStatus.type === 'success'
                  ? 'bg-green-50 border-green-500'
                  : 'bg-red-50 border-red-500'
              }`}
            >
              <div className="flex items-center space-x-3">
                {inquiryStatus.type === 'success' ? (
                  <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0" />
                ) : (
                  <Mail className="w-6 h-6 text-red-600 flex-shrink-0" />
                )}
                <div>
                  <h3
                    className={`font-bold text-lg ${
                      inquiryStatus.type === 'success' ? 'text-green-800' : 'text-red-800'
                    }`}
                  >
                    {inquiryStatus.type === 'success' ? 'Request Submitted!' : 'Submission Failed'}
                  </h3>
                  <p
                    className={`text-sm ${
                      inquiryStatus.type === 'success' ? 'text-green-700' : 'text-red-700'
                    }`}
                  >
                    {inquiryStatus.message}
                  </p>
                </div>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Personal Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-gray-700 font-semibold block mb-2">Full Name *</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="John Smith"
                  required
                  disabled
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl text-sm bg-gray-100 text-gray-600 cursor-not-allowed"
                  style={{
                    outline: 'none',
                  }}
                />
              </div>

              <div>
                <label className="text-sm text-gray-700 font-semibold block mb-2">Phone Number *</label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  placeholder="(555) 123-4567"
                  required
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl text-sm"
                  style={{
                    outline: 'none',
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = taxlatesOrange;
                    e.target.style.boxShadow = `0 0 0 3px ${taxlatesOrange}20`;
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#d1d5db';
                    e.target.style.boxShadow = 'none';
                  }}
                />
              </div>
            </div>

            <div>
              <label className="text-sm text-gray-700 font-semibold block mb-2">Email Address *</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="john@example.com"
                required
                disabled
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl text-sm bg-gray-100 text-gray-600 cursor-not-allowed"
                style={{
                  outline: 'none',
                }}
              />
            </div>

            {/* Message Box */}
            <div>
              <label className="text-sm text-gray-700 font-semibold block mb-2">Message (Optional)</label>
              <textarea
                name="message"
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                placeholder="Tell us more about your needs or ask any questions..."
                rows={4}
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl text-sm"
                style={{
                  outline: 'none',
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = taxlatesOrange;
                  e.target.style.boxShadow = `0 0 0 3px ${taxlatesOrange}20`;
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#d1d5db';
                  e.target.style.boxShadow = 'none';
                }}
              />
            </div>

            {/* Target Location */}
            <div className="pt-4 border-t border-gray-200">
              <h3 className="text-sm font-bold text-gray-700 mb-3">Target Location</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-gray-700 font-semibold block mb-2">State *</label>
                  <select
                    name="state"
                    value={formData.state}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl text-sm"
                    style={{
                      outline: 'none',
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = taxlatesOrange;
                      e.target.style.boxShadow = `0 0 0 3px ${taxlatesOrange}20`;
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#d1d5db';
                      e.target.style.boxShadow = 'none';
                    }}
                  >
                    <option value="">Select State</option>
                    {states.map((state) => (
                      <option key={state} value={state}>{state}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="text-sm text-gray-700 font-semibold block mb-2">County *</label>
                  <select
                    name="county"
                    value={formData.county}
                    onChange={handleInputChange}
                    required
                    disabled={!formData.state}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
                    style={{
                      outline: 'none',
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = taxlatesOrange;
                      e.target.style.boxShadow = `0 0 0 3px ${taxlatesOrange}20`;
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#d1d5db';
                      e.target.style.boxShadow = 'none';
                    }}
                  >
                    <option value="">
                      {formData.state ? 'Select County' : 'Select a state first'}
                    </option>
                    {formCounties.map((county) => (
                      <option key={county} value={county}>{county}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <div className="pt-6 border-t border-gray-200">
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full flex items-center justify-center space-x-2 px-6 py-4 text-white rounded-xl transition-all font-bold shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                style={{
                  backgroundColor: isSubmitting ? '#9ca3af' : taxlatesOrange,
                }}
                onMouseEnter={(e) => {
                  if (!isSubmitting) {
                    e.currentTarget.style.backgroundColor = '#d4552e';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isSubmitting) {
                    e.currentTarget.style.backgroundColor = taxlatesOrange;
                  }
                }}
              >
                {isSubmitting ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    <span>Submitting...</span>
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5" />
                    <span>Submit Request</span>
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* My Orders Section */}
      <div data-section="my-orders">
        <MyOrders highlightOrderId={initialParams?.orderId} />
      </div>

      {/* Thank You Modal */}
      {showThankYouModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 relative animate-in fade-in duration-200">
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-10 h-10 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold mb-3" style={{ color: taxlatesNavy }}>
                Thank you for your message
              </h2>
              <p className="text-gray-600 text-lg mb-6">
                We will respond within 4 hours.
              </p>
              <button
                onClick={() => setShowThankYouModal(false)}
                className="w-full px-6 py-3 text-white rounded-xl font-bold transition-all"
                style={{ backgroundColor: taxlatesOrange }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#d4552e';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = taxlatesOrange;
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
